import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-contrib-ads';
import 'videojs-ima';
import 'videojs-hls-quality-selector';
import { checkOperatingSystem } from "../../utils/utils";
import { ssaiAdParam } from '../../utils/utils';
import { getPlayerToken } from "../../network/service";
import { capitalize } from '@mui/material';

export const VideoJS = (props) => {
  const [controlColor, setControlColor] = useState();
  const [skipOverlay, setSkipOverlay] = useState(null); // {dir: 'forward'|'backward', amount: number} | null
  const [showSkipButtons, setShowSkipButtons] = useState(false);
  const skipButtonTimeoutRef = useRef();
  const skipTimeoutRef = useRef();
  const skipAmountRef = useRef({ dir: null, amount: 0 });
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const videoRef = React.useRef(null);
  const playerRef = React.useRef(null);
  // const pauseRef = React.useRef(null);
  const { options, onReady, pauseVideo, isTrailer, poster, is_live, isRadioLive, isPlaylist } = props;

  const location = useLocation()
  let uid = localStorage.getItem("gid");
  let user_id = localStorage.getItem("userId");
  let playlists = null;

  if (user_id) {
    uid = user_id
  }
  const appInfo = {
    projectDetails: projectInfo
  }

  if (videojs.Vhs) {
    videojs.Vhs.xhr.beforeRequest = function (options) {
      if (options?.uri.includes(".m3u8?id=")) {
        options.headers = {
          pubid: projectInfo?.projectConfig?.pubid,
          channelid: projectInfo?.projectConfig?.channelid,
          uid: uid,
          "access-token": accessToken,
        };
      }
      return options;
    };
  } else {
    console.warn("VHS plugin is not available in this version of Video.js.");
  }

  // Show/hide skip buttons on hover/touch, hide after 5s inactivity or on blur/mouseleave
  const handleShowSkipButtons = () => {
    setShowSkipButtons(true);
    clearTimeout(skipButtonTimeoutRef.current);
    skipButtonTimeoutRef.current = setTimeout(() => setShowSkipButtons(false), 5000);
  };

  const handleHideSkipButtons = () => {
    setShowSkipButtons(false);
    clearTimeout(skipButtonTimeoutRef.current);
  };

  React.useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const mobileControls = [
        'playToggle',
        'progressControl',
        'fullscreenToggle',
        'playbackRateMenuButton',
        'qualitySelector'
      ];
      const desktopControls = [
        'playToggle',
        'volumePanel',
        'progressControl',
        'remainingTimeDisplay',
        'fullscreenToggle',
        'playbackRateMenuButton',
        'subsCapsButton',
        'settingsMenuButton',
        'qualitySelector'
      ];
      const controlBarChildren = isMobile ? mobileControls : desktopControls;
      if (props?.liveData?.channel_id) {
        if (props?.liveData?.ssai_enabled) {
          ssaiAdParam(props?.liveData, appInfo).then((response) => {
            createSSAI(props?.liveData.ssai_link, videoElement, response, false)
          })
        } else {
          const player = playerRef.current = videojs(videoElement, {
            ...options,
            autoplay: isTrailer || is_live || isPlaylist ? true : false,
            playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
            controlBar: {
              children: controlBarChildren
            },
            html5: {
              vhs: {
                cacheEncryptionKeys: true
              }
            }
          }, () => {
            // Enable quality selector
            player.hlsQualitySelector && player.hlsQualitySelector({
              displayCurrentQuality: true,
              selected: false,
            });
            onReady && onReady(player);
          });
        }
      } else {
        const player = playerRef.current = videojs(videoElement, {
          ...options,
          autoplay: isTrailer || is_live || isPlaylist ? true : false,
          playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
          controlBar: {
            children: controlBarChildren
          },
          html5: {
            vhs: {
              cacheEncryptionKeys: true
            }
          }
        }, () => {
          // Enable quality selector
          player.hlsQualitySelector && player.hlsQualitySelector({
            displayCurrentQuality: true,
            selected: false
          });

          // Add custom Quality menu button
          const MenuButton = videojs.getComponent('MenuButton');
          const MenuItem = videojs.getComponent('MenuItem');
          // Helper: get loaded playlists
          function getLoadedPlaylists(player) {
            if (player.tech_ && player.tech_.vhs && player.tech_.vhs.playlists && player.tech_.vhs.playlists.master) {
              return player.tech_.vhs.playlists.master.playlists;
            } else if (player.tech_ && player.tech_.hls && player.tech_.hls.playlists && player.tech_.hls.playlists.master) {
              return player.tech_.hls.playlists.master.playlists;
            }
            return null;
          }
          // Helper: find playlist by resolution type
          function findPlaylistByType(playlists, type) {
            if (!playlists) return null;
            for (let i = 0; i < playlists.length; i++) {
              const p = playlists[i];
              if (p.attributes && p.attributes.RESOLUTION) {
                const resString = p.attributes.RESOLUTION.height + 'p';
                if (resString === type) return p;
              }
            }
            return null;
          }
          // Helper: switch player source to playlist
          function switchToPlaylist(player, playlist, type) {
            if (!playlist) return;
            let baseUrl = '';
            if (props.videoUrl) {
              const url = new URL(props.videoUrl, window.location.href);
              baseUrl = url.origin + url.pathname.substring(0, url.pathname.lastIndexOf('/') + 1);
            }
            let playlistUri = playlist.uri;
            let urlWithToken = playlistUri.startsWith('http') ? playlistUri : baseUrl + playlistUri;
            localStorage.setItem('videoSelectedResolution', type);
            const currentTime = player.currentTime();
            const wasPlaying = !player.paused();
            player.src({ src: urlWithToken, type: 'application/x-mpegURL' });
            player.one('loadedmetadata', function () {
              player.currentTime(currentTime);
              if (wasPlaying) player.play();
            });
            return urlWithToken;
          }
          const QualityMenuButton = videojs.extend(MenuButton, {
            constructor: function (player, options) {
              MenuButton.call(this, player, options);
              const storedRes = localStorage.getItem('videoSelectedResolution');
              this.currentQuality = storedRes || 'Auto';
              this.updateButtonText();
              // Auto-switch to stored resolution after playlist loads
              if (storedRes) {
                player.one('loadedmetadata', function () {
                  playlists = getLoadedPlaylists(player);
                  const foundPlaylist = findPlaylistByType(playlists, storedRes);
                  if (foundPlaylist) {
                    switchToPlaylist(player, foundPlaylist, storedRes);
                    this.currentQuality = storedRes;
                    this.updateButtonText();
                  }
                }.bind(this));
              }
            },
            createItems: function () {
              const items = [];
              const self = this;
              const resolutions = (props && props.videoResolutionInfo) ? props.videoResolutionInfo : [];
              resolutions.forEach(res => {
                const item = new MenuItem(player, {
                  label: capitalize(res.name),
                  selectable: true,
                  selected: this.currentQuality === res.type
                });
                item.on('click', function () {
                  try {
                    if (!playlists) {
                      playlists = getLoadedPlaylists(player);
                    }

                    // Remove selected from all items
                    items.forEach(i => i.selected(false));
                    // Set selected for this item
                    item.selected(true);

                    const foundPlaylist = findPlaylistByType(playlists, res.type);
                    if (foundPlaylist) {
                      switchToPlaylist(player, foundPlaylist, res.type);
                      self.currentQuality = res.type;
                      self.updateButtonText();
                    } else {
                      console.warn('Could not find matching loaded playlist for', res.type);
                    }
                  } catch (err) {
                    console.error('Error switching quality:', err);
                  }
                });
                items.push(item);
              });
              return items;
            },
            updateButtonText: function () {
              this.el().querySelector('.vjs-icon-placeholder').textContent = this.currentQuality;
            },
            handleClick: function () {
              MenuButton.prototype.handleClick.call(this);
              this.updateButtonText();
            }
          });
          videojs.registerComponent('QualityMenuButton', QualityMenuButton);
          player.controlBar.addChild('QualityMenuButton', {}, player.controlBar.children().length - 1);
          onReady && onReady(player);

          onReady && onReady(player);
        });
      }
    } else {
      //   const player = playerRef.current;
      //   player.autoplay(options.autoplay);
      //   player.src(options.sources);
    }

    // const fetchPlayerToken = async (videoId) => {
    //   const playerTokenResponse = await getPlayerToken(appInfo, videoId);
    //   if (playerTokenResponse?.status === 200) {
    //     return playerTokenResponse?.data?.data;
    //   }
    // };

    // Add keydown event listener for skip overlay
    const handleFullscreenChange = () => {
      if (videoRef.current) {
        videoRef.current.focus();
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    const handleKeyDown = (e) => {
      if (!playerRef.current) return;
      if (document.activeElement && document.activeElement.tagName === 'INPUT') return;
      let update = false;
      if (e.key === 'ArrowRight') {
        if (videoRef.current) videoRef.current.focus();
        if (skipAmountRef.current.dir === 'forward') {
          skipAmountRef.current.amount += 10;
        } else {
          skipAmountRef.current.dir = 'forward';
          skipAmountRef.current.amount = 10;
        }
        setSkipOverlay({ dir: 'forward', amount: skipAmountRef.current.amount });
        update = true;
      } else if (e.key === 'ArrowLeft') {
        if (videoRef.current) videoRef.current.focus();
        if (skipAmountRef.current.dir === 'backward') {
          skipAmountRef.current.amount += 10;
        } else {
          skipAmountRef.current.dir = 'backward';
          skipAmountRef.current.amount = 10;
        }
        setSkipOverlay({ dir: 'backward', amount: skipAmountRef.current.amount });
        update = true;
      }
      if (update) {
        clearTimeout(skipTimeoutRef.current);
        skipTimeoutRef.current = setTimeout(() => {
          setSkipOverlay(null);
          skipAmountRef.current = { dir: null, amount: 0 };
        }, 1500);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    // Show/hide skip buttons on mousemove/touchstart/blur/mouseleave
    const playerDiv = videoRef.current?.parentNode;
    if (playerDiv) {
      playerDiv.addEventListener('mousemove', handleShowSkipButtons);
      playerDiv.addEventListener('touchstart', handleShowSkipButtons);
      playerDiv.addEventListener('mouseleave', handleHideSkipButtons);
      playerDiv.addEventListener('blur', handleHideSkipButtons);
    }
    window.addEventListener('blur', handleHideSkipButtons);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      clearTimeout(skipTimeoutRef.current);
      clearTimeout(skipButtonTimeoutRef.current);
      if (playerDiv) {
        playerDiv.removeEventListener('mousemove', handleShowSkipButtons);
        playerDiv.removeEventListener('touchstart', handleShowSkipButtons);
        playerDiv.removeEventListener('mouseleave', handleHideSkipButtons);
        playerDiv.removeEventListener('blur', handleHideSkipButtons);
      }
      window.removeEventListener('blur', handleHideSkipButtons);
    };
  }, [options, videoRef]);

  useEffect(() => {
    let clear = true
    if (props?.liveData) {
      createSSAI("", "", "", clear)

    }
    return () => {
      createSSAI("", "", "", clear)
    }
  }, [props?.liveData])

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player = playerRef.current;

    if (props?.adUrl) {
      // var imaOptions = {
      //   adTagUrl: props?.adUrl
      // };

      // player.ima(imaOptions)

      player.on('error', () => {
        const error = player.error(); // Get the error details
        if (error) {
          console.log("Video.js Error: ", error);

          // Example: Logging specific error codes
          console.log(`Error Code: ${error.code} - ${error.message || "No message provided"}`);

          if (error.code === 4) {
            console.log("MEDIA_ERR_SRC_NOT_SUPPORTED: The video source is unsupported.");
          }
        }
      });

      // Pause the video when ad starts
      // player.on('ads-ad-started', () => {
      //   const videoElement = videoRef.current;
      //   // console.log('ads-ad-started', videoElement);

      //   try {
      //     // console.log('Before pause:', player.paused());  // Log current state
      //     document.getElementById('vjs_video_player_html5_api').pause()
      //     // console.log('After pause:', player.paused());  // Log state after pause
      //   } catch (error) {
      //     console.log('error', error);

      //   }




      //   // videoElement.pause();
      // });

      player.on('play', () => {
        const posterImage = player.el().querySelector('.vjs-poster');
        if (posterImage && isRadioLive) {
          // posterImage.style.display = 'block'; // Keep the poster visible
        }
      });

      // Resume the video when ad ends
      // player.on('ads-ad-ended', () => {
      //   // console.log('ads-ad-ended');

      //   player.play();
      // });

      // player.on('ima3-started', () => {
      //   // console.log('ima3 Ad started');
      //   player.pause(); // Pause video when ad starts
      // });

      // player.on('ima3-complete', () => {
      //   // console.log('ima3 Ad completed');
      //   player.play(); // Resume video when ad ends
      // });

    }

    return () => {
      if (player) {
        player.dispose();
        playerRef.current = null;

      }
    };


  }, [videoRef]);

  useEffect(() => {
    setControlColor('#fff');
  }, [projectInfo]);

  function createSSAI(url, videoElem, adsParams, clearFlag) {


    let eachSecondInterval;
    let refreshInterval;
    if (clearFlag === false) {

      sessionCreation(url);

      var arr = url.split("/");
      var result = arr[0] + "//" + arr[2]
      var domainOfUrl = result;



      var previousTime = 0;
      var beaconTime = [];
      var trackingUrl = domainOfUrl;
      var beaconData = []
      var whereYouAt = 0;
      eachSecondInterval = setInterval(eachSecond, 1000);




      function isInArray(value, array) {
        return array.indexOf(value);
      }


      function eachSecond() {


        whereYouAt = whereYouAt + 1;
        whereYouAt = Math.round(whereYouAt)



        if (whereYouAt != previousTime) {
          previousTime = whereYouAt;

          if (beaconTime.length > 0) {

            if (isInArray(whereYouAt, beaconTime) > -1) {

              var temp = beaconData[isInArray(whereYouAt, beaconTime)].beaconUrls;
              for (var i = 0; i < temp.length; i++) {

                callAdTrackingUrl(temp[i])
              }



            }



          }

        }
      }


      function callAPI(url) {

        var request = new XMLHttpRequest();
        request.onreadystatechange = function () {
          if (request.readyState == 4 && request.status == 200) {


            var trackingData = JSON.parse(request.responseText);

            var avails = trackingData.avails;



            for (let k = 0; k < avails.length; k++) {


              var avail = avails[k];

              var ads = avail.ads;


              for (let j = 0; j < ads.length; j++) {

                var trackingEvents = ads[j].trackingEvents;

                for (k = 0; k < trackingEvents.length; k++) {

                  var trackingEvent = trackingEvents[k]



                  if (trackingEvent && trackingEvent.eventType) {
                    var eventType = trackingEvent.eventType;
                    if (eventType == "start" || eventType == "firstQuartile" || eventType == "firstQuartile" || eventType == "midpoint" || eventType == "impression") {
                      beaconTime.push(Math.round(trackingEvent.startTimeInSeconds))
                      beaconData.push(trackingEvent)


                    }
                  }

                }



              }




            }


          } else if (request.status == 502) {

            beaconTime = [];

          }

        }
        request.open("GET", url);
        request.send(null);
      }

      function sessionCreation(theUrl) {

        var data = JSON.stringify(adsParams);

        var xhr = new XMLHttpRequest();


        xhr.addEventListener("readystatechange", function () {
          if (this.readyState === 4) {
            var jsonResult = JSON.parse(this.responseText)


            // const player = videojs(videoElem);

            const player = playerRef.current = videojs(videoElem, { ...options, html5: { vhs: { cacheEncryptionKeys: true } } }, () => {
              onReady && onReady(player);
            });

            if (jsonResult) {
              player.src({
                src: domainOfUrl + jsonResult.manifestUrl,
                type: 'application/x-mpegURL',
              });
              trackingUrl = trackingUrl + jsonResult.trackingUrl;
              refreshInterval = setInterval(refresh, 10000);
            }




          }
        });

        xhr.open("POST", theUrl);
        xhr.setRequestHeader("content-type", "application/json");


        xhr.send(data);


      }

      function refresh() {
        callAPI(trackingUrl);
      }



      function callAdTrackingUrl(theUrl) {


        var i = document.createElement("img");
        i.src = theUrl;



      }
    } else if (clearFlag === true) {
      clearTimeout(eachSecondInterval)
      clearTimeout(refreshInterval)
    }




  }
  return (
    <div data-vjs-player style={{ position: 'relative' }}>
      <style>{`
        .video-js .vjs-control-bar {
          height: 50px !important;
        }
        .video-js .vjs-progress-holder,
        .video-js .vjs-progress-control {
          height: 12px !important;
          min-height: 12px !important;
        }
        .video-js .vjs-play-progress,
        .video-js .vjs-load-progress,
        .video-js .vjs-slider-bar {
          height: 12px !important;
          min-height: 12px !important;
        }
        .video-js .vjs-volume-bar,
        .video-js .vjs-volume-level {
          height: 12px !important;
          min-height: 12px !important;
        }
          .video-js .vjs-menu-content {
          font-size: 1.25em !important;
          padding: 12px 0 !important;
        }
          .video-js .vjs-control-bar {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            gap: 12px !important;
          }
          .video-js .vjs-control-bar > * {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
        .video-js .vjs-progress-control,
        .video-js .vjs-control {
          align-self: center !important;
        }
        .video-js .vjs-control .vjs-icon-placeholder,
        .video-js .vjs-control .vjs-icon-play,
        .video-js .vjs-control .vjs-icon-pause,
        .video-js .vjs-control .vjs-icon-volume-high,
        .video-js .vjs-control .vjs-icon-captions,
        .video-js .vjs-control .vjs-icon-fullscreen-enter,
        .video-js .vjs-control .vjs-icon-fullscreen-exit,
        .video-js .vjs-control .vjs-icon-subtitles,
        .video-js .vjs-control .vjs-icon-settings,
        .video-js .vjs-control .vjs-icon-share,
        .video-js .vjs-control .vjs-icon-download {
          font-size: 150% !important;
        }
          .vjs-playback-rate {
          font-size: 110% !important;
        }
        .video-js .vjs-slider .vjs-slider-bar {
          background-color: ${controlColor};
        }
        .video-js .vjs-play-progress {
          background-color: ${controlColor};
        }
        .vjs-control:hover {
          color: ${controlColor}; 
        }
        .vjs-volume-level {
          background-color: ${controlColor} !important;
        }
        .vjs-control:hover > .vjs-icon-placeholder,
        .vjs-control:hover > .vjs-icon-play,
        .vjs-control:hover > .vjs-icon-pause,
        .vjs-control:hover > .vjs-icon-volume-high,
        .vjs-control:hover > .vjs-icon-captions,
        .vjs-control:hover > .vjs-icon-fullscreen-enter,
        .vjs-control:hover > .vjs-icon-fullscreen-exit {
          background: ${controlColor}; 
          color: ${controlColor}; 
        }
        .vjs-layout-small .vjs-progress-control,
        .vjs-layout-tiny .vjs-progress-control {
          display: flex !important;
        }
        .vjs-progress-control,
        .vjs-progress-control * {
          display: flex !important;
        }
          .video-js .vjs-volume-control,
        .video-js .vjs-volume-bar,
        .video-js .vjs-volume-level {
          align-self: center !important;
        }
          .video-js .vjs-play-progress:before {
          width: 50px !important;
          height: 50px !important;
          left: -9px !important;
        }
                  /* Hide Video.js description text */
        .video-js .vjs-description {
          display: none !important;
        }
           .video-js .vjs-playback-rate.vjs-menu-button {
          position: relative !important;
          top: 5px !important;
          padding: 0 5px !important;
        }
          .video-js .vjs-progress-control,
        .video-js .vjs-progress-control{
          font-size: 1.25em !important;
        }
          .video-js .vjs-volume-level:before {
            display: none !important;
          }
          .video-js .vjs-play-progress:before {
            display: none !important;
          }
         
            .vjs-menu-button-popup .vjs-menu .vjs-menu-content{
                font-size: 12px !important;
            }
                .video-js .vjs-subs-caps-button {
          margin-right: 35px !important;
        }

        @media (max-width: 600px) {
          .video-js .vjs-playback-rate.vjs-menu-button {
            z-index: 9999 !important;
            min-width: 48px !important;
            min-height: 48px !important;
            font-size: 1.2em !important;
          }
          .video-js .vjs-playback-rate .vjs-menu {
            z-index: 9999 !important;
          }
        }
        .video-js .vjs-time-control {
          font-size: 2em;
        }
      `}</style>
      {/* Skip overlay */}
      {/* Forward/Backward skip buttons (show on hover/touch) */}
      {showSkipButtons && (
        <>
          <button
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.4)',
              border: 'none',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
              cursor: 'pointer',
              outline: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            onClick={() => {
              // Simulate left arrow skip
              if (!playerRef.current) return;
              if (skipAmountRef.current.dir === 'backward') {
                skipAmountRef.current.amount += 10;
              } else {
                skipAmountRef.current.dir = 'backward';
                skipAmountRef.current.amount = 10;
              }
              setSkipOverlay({ dir: 'backward', amount: skipAmountRef.current.amount });
              playerRef.current.currentTime(playerRef.current.currentTime() - 10);
              clearTimeout(skipTimeoutRef.current);
              skipTimeoutRef.current = setTimeout(() => {
                setSkipOverlay(null);
                skipAmountRef.current = { dir: null, amount: 0 };
              }, 1500);
            }}
            tabIndex={-1}
            aria-label="Skip backward 10 seconds"
          >
            <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="#fff" fillOpacity="0.15" />
              <path d="M30 34L18 24L30 14" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.4)',
              border: 'none',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
              cursor: 'pointer',
              outline: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            onClick={() => {
              // Simulate right arrow skip
              if (!playerRef.current) return;
              if (skipAmountRef.current.dir === 'forward') {
                skipAmountRef.current.amount += 10;
              } else {
                skipAmountRef.current.dir = 'forward';
                skipAmountRef.current.amount = 10;
              }
              setSkipOverlay({ dir: 'forward', amount: skipAmountRef.current.amount });
              playerRef.current.currentTime(playerRef.current.currentTime() + 10);
              clearTimeout(skipTimeoutRef.current);
              skipTimeoutRef.current = setTimeout(() => {
                setSkipOverlay(null);
                skipAmountRef.current = { dir: null, amount: 0 };
              }, 1500);
            }}
            tabIndex={-1}
            aria-label="Skip forward 10 seconds"
          >
            <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="#fff" fillOpacity="0.15" />
              <path d="M18 14L30 24L18 34" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </>
      )}
      {skipOverlay && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: skipOverlay.dir === 'backward' ? '5%' : 'auto',
            right: skipOverlay.dir === 'forward' ? '5%' : 'auto',
            transform: 'translateY(-50%)',
            background: 'rgba(0, 0, 0, 0.03)',
            // background: 'transparent',
            color: '#fff',
            fontSize: 45,
            borderRadius: 50,
            padding: '10px 15px',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            pointerEvents: 'none',
            fontWeight: 600,
            minWidth: 100,
            justifyContent: 'center',
            transition: 'left 0.2s, right 0.2s'
          }}
        >
          {skipOverlay.dir === 'backward' ? (
            <>
              <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 6, animation: 'bounceLeft 0.5s' }}>
                <circle cx="24" cy="24" r="24" fill="#fff" fillOpacity="0.15" />
                <path d="M30 34L18 24L30 14" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 20 }}>{'-'}{skipOverlay.amount}</span>
            </>
          ) : (
            <>
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 20 }}>{'+'}{skipOverlay.amount}</span>
              <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: 6, animation: 'bounceRight 0.5s' }}>
                <circle cx="24" cy="24" r="24" fill="#fff" fillOpacity="0.15" />
                <path d="M18 14L30 24L18 34" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>

            </>
          )}
          {/* <span style={{ color: '#fff', fontWeight: 600, fontSize: 20 }}>{skipOverlay.dir === 'forward' ? '+' : '-'}{skipOverlay.amount}</span> */}
          {/* SVG bounce animation keyframes */}
          <style>
            {`
            @keyframes bounceLeft {
              0% { transform: translateX(-20px) scale(0.8); }
              60% { transform: translateX(0px) scale(1.1); }
              100% { transform: translateX(0px) scale(1); }
            }
            @keyframes bounceRight {
              0% { transform: translateX(20px) scale(0.8); }
              60% { transform: translateX(0px) scale(1.1); }
              100% { transform: translateX(0px) scale(1); }
            }
          `}
          </style>
        </div>
      )}
      <video ref={videoRef} id='vjs_video_player' className='video-js vjs-big-play-centered' poster={poster} tabIndex={0} >
        {
          props?.closedCaptions?.map((item, index) => (
            <track key={index} kind='captions' src={item?.closed_caption_url} srclang={item?.short_code} label={item?.language_name} default={projectInfo?.projectConfig?.config?.DEFAULT_CAPTIONS_ON == "true" ? true : false} />
          ))
        }
        {
          props?.subtitles?.map((item, index) => (
            <track key={index} kind='captions' src={item?.subtitle_url} srclang={item?.short_code} label={item?.language_name} default={projectInfo?.projectConfig?.config?.DEFAULT_CAPTIONS_ON == "true" ? true : false} />
          ))
        }
      </video>
    </div>
  );
}

export default VideoJS;
